/**
 * #270 — rows loaded through the lean list API carry no `linkPreview.thumbnail`
 * (`thumbnailOmitted` / `hasThumbnail` instead). The UI fetches the stored thumbnail by id when
 * it actually renders one. Results are cached (bounded) and concurrent requests for one id are
 * shared. Never throws: a failed lookup resolves undefined and is NOT cached, so a later render retries.
 */
import type { UserMessage } from '@bt-common/schemas/bt/user-message';

export type FetchDocsById = (id: string) => Promise<UserMessage[] | null | undefined>;

const MAX_CACHE = 200;
const cache = new Map<string, string>();
const inflight = new Map<string, Promise<string | undefined>>();

export const resetLazyThumbnailForTests = (): void => { cache.clear(); inflight.clear(); };

export const peekThumbnail = (id: string): string | undefined => cache.get(id);

type PreviewShape = { thumbnail?: unknown; thumbnailOmitted?: boolean } | null | undefined;

/** True when the row's preview image was withheld by the lean list API. */
export const isThumbnailOmitted = (m: { linkPreview?: unknown; hasThumbnail?: boolean } | null | undefined): boolean => {
    const lp = m?.linkPreview as PreviewShape;
    if (lp && typeof lp.thumbnail === 'string' && lp.thumbnail) return false;
    return lp?.thumbnailOmitted === true || m?.hasThumbnail === true;
};

export const fetchLazyThumbnail = (id: string, fetchById: FetchDocsById): Promise<string | undefined> => {
    const hit = cache.get(id);
    if (hit) return Promise.resolve(hit);
    const pending = inflight.get(id);
    if (pending) return pending;
    const p = (async () => {
        try {
            const docs = await fetchById(id);
            const thumb = Array.isArray(docs) ? (docs[0]?.linkPreview as PreviewShape)?.thumbnail : undefined;
            if (typeof thumb !== 'string' || !thumb) return undefined;
            if (cache.size >= MAX_CACHE) cache.delete(cache.keys().next().value as string);
            cache.set(id, thumb);
            return thumb;
        } catch {
            return undefined;
        } finally {
            inflight.delete(id);
        }
    })();
    inflight.set(id, p);
    return p;
};

/**
 * #270: a CREATE built from a lean row would save the replacement WITHOUT its image (the BE restores
 * an omitted thumbnail only on patch/update, never on create), and the split flows then delete the
 * original. So put the stored thumbnail back first. Throws when the row is lean and the stored
 * thumbnail cannot be fetched — callers create BEFORE deleting the original, so aborting is safe.
 * Rows that are not lean are returned untouched.
 */
export const withFullThumbnailForCreate = async <T extends object>(
    row: T,
    sourceId: string | undefined,
    fetchById: FetchDocsById,
): Promise<T> => {
    if (!isThumbnailOmitted(row as { linkPreview?: unknown; hasThumbnail?: boolean })) return row;
    const lean = row as { linkPreview?: unknown; hasThumbnail?: boolean };
    const thumb = sourceId ? await fetchLazyThumbnail(sourceId, fetchById) : undefined;
    if (!thumb) throw new Error(`[#270] cannot restore the link-preview thumbnail for ${sourceId ?? 'unknown id'}; aborting before it is lost`);
    const { thumbnailOmitted: _omitted, ...preview } = (lean.linkPreview ?? {}) as Record<string, unknown>;
    const { hasThumbnail: _has, ...rest } = row as T & { hasThumbnail?: boolean };
    return { ...rest, linkPreview: { ...preview, thumbnail: thumb } } as unknown as T;
};
